import { Dialog, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, createRoutesFromElements, Outlet, Route, RouterProvider } from "react-router-dom";
import axios from "./api/axios";
import hbscan from "./api/hbscan";
import MainLayout from "./components/layout/MainLayout";
import ModalCard from "./components/modal/ModalCard";
import EditOnlyRouteGuard from "./components/permissions/EditOnlyRouteGuard";
import { ProtectedRoute } from "./components/routes/protectedRoute/ProtectedRoute";
import useConnexionState from "./hooks/useConnexionState";
import { LoadingProvider } from "./hooks/useLoadingContext";
import { AllHBscanRoles, ERole } from "./models/enums/role";
import GlobalDashboardPage from "./pages/dashboard/GlobalDashboardPage";
import PeriodicDashboardPage from "./pages/dashboard/PeriodicDashboardPage";
import WasteCanDashboardPage from "./pages/dashboard/WasteCanDashboardPage";
import EslTemplateEditor from "./pages/esl/EslTemplateEditor";
import EslTemplatesPage from "./pages/esl/EslTemplatesPage";
import FilledFormPage from "./pages/forms/filledFormPage";
import FilledFormsPage from "./pages/forms/filledFormsPage";
import FormTemplateEditPage from "./pages/forms/formTemplateEditPage";
import FormTemplatesPage from "./pages/forms/formTemplatesPage";
import ServiceRequestPage from "./pages/forms/serviceRequest/serviceRequestPage";
import DataPage from "./pages/landing/dataPage";
import HBscanPage from "./pages/landing/hbscanPage";
import ScansPage from "./pages/landing/scansPage";
import TablePage from "./pages/landing/tablePage";
import TimesheetPage from "./pages/landing/timesheetPage";
import WorkingRoadPage from "./pages/landing/workingRoadPage";
import LoginPage from "./pages/login/loginPage";
import RegisterPage from "./pages/login/registerPage";
import PeriodicAddPage from "./pages/periodic/periodicAddPage";
import PeriodicPage from "./pages/periodic/periodicPage";
import ForgotPasswordPage from "./pages/reset/forgotPasswordPage";
import StepperSensorPage from "./pages/sensor/stepperSensorPage";
import ErrorScreen from "./pages/service+/errorScreen";
import InstallationPage from "./pages/service+/InstallationPage";
import SuccessScreen from "./pages/service+/successScreen";
import BuildingAddPage from "./pages/table/building/BuildingAddPage";
import BuildingEditPage from "./pages/table/building/BuildingEditPage";
import BuildingPage from "./pages/table/building/BuildingPage";
import CompanyEditPage from "./pages/table/company/CompanyEditPage";
import ContactAddPage from "./pages/table/contact/ContactAddPage";
import ContactEditPage from "./pages/table/contact/ContactEditPage";
import ContactPage from "./pages/table/contact/ContactPage";
import FloorAddPage from "./pages/table/floor/FloorAddPage";
import FloorEditPage from "./pages/table/floor/FloorEditPage";
import FloorPage from "./pages/table/floor/FloorPage";
import PropertyGroupAddPage from "./pages/table/groupProperty/PropertyGroupAddPage";
import PropertyGroupEditPage from "./pages/table/groupProperty/PropertyGroupEditPage";
import PropertyGroupPage from "./pages/table/groupProperty/PropertyGroupPage";
import LocalAddPage from "./pages/table/local/LocalAddPage";
import LocalEditPage from "./pages/table/local/LocalEditPage";
import LocalPage from "./pages/table/local/LocalPage";
import ProfileAddPage from "./pages/table/profile/ProfileAddPage";
import ProfileEditPage from "./pages/table/profile/ProfileEditPage";
import ProfilePage from "./pages/table/profile/ProfilePage";
import RouteAddPage from "./pages/table/route/RouteAddPage";
import RouteEditPage from "./pages/table/route/RouteEditPage";
import RoutePage from "./pages/table/route/RoutePage";
import TaskPage from "./pages/table/task/TaskPage";
import DetailedTicketPage from "./pages/table/ticket/DetailedTicketPage";
import TicketsPage from "./pages/table/ticket/TicketsPage";
import UserAddPage from "./pages/table/user/UserAddPage";
import UserEditPage from "./pages/table/user/UserEditPage";
import UserPage from "./pages/table/user/UserPage";
import { routes } from "./routes";
import AuthService from "./services/auth/AuthService";
import PeriodicReviewManager from "./pages/periodic/PeriodicReviewManager";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CACHE_TIMES } from "./utils/queryConstants";


const NotFound = () => {
    return <Typography variant="h6">404 Not found</Typography>;
};

const ConnexionModal = () => {

    const [isShowModal, setIsShowModal] = useState(false);
    const { isOnline } = useConnexionState();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isOnline) setIsShowModal(true)
    }, [isOnline])

    return (
        <>
            <Dialog open={isShowModal} onClose={() => setIsShowModal(false)}>
                <ModalCard sx={{ borderRadius: 4, width: 550, height: '100%', overflow: 'auto' }} title={t("message.error")} closeCallback={() => setIsShowModal(false)}>
                    {t("message.noConnexion")}
                </ModalCard>
            </Dialog>
            <Outlet />
        </>
    )
};

const getRoutesForRole = () => {
    const userRole = AuthService.user?.role;

    return (
        <Route
            element={
                <LoadingProvider instances={[axios, hbscan]} initialBypass={['/postlogin']}>
                    <ConnexionModal />
                </LoadingProvider>
            }
        >
            {/* public routes */}
            <Route path={routes.login} element={<LoginPage />} />
            <Route path={routes.register} element={<RegisterPage />} />
            <Route path={routes.emailReset} element={<ForgotPasswordPage />} />
            <Route
                path={routes.forms.serviceRequest}
                element={<ServiceRequestPage />}
            />

            {/* private routes */}
            <Route element={<ProtectedRoute allowedRoles={AllHBscanRoles} />}>
                <Route element={<EditOnlyRouteGuard />}>
                <Route element={<MainLayout />}>
                    <Route path={routes.home} element={<HBscanPage />} />

                    {/* Data Routes - show all for non-clients, limited for clients */}
                    <Route path={routes.dataRoute.data}>
                        <Route index element={<DataPage />} />

                        {/* Routes visible to all roles */}
                        <Route
                            path={routes.dataRoute.interactiveMap}
                            element={<HBscanPage />}
                        />
                        <Route
                            path={routes.dataRoute.globalDashboard}
                            element={<GlobalDashboardPage />}
                        />
                        <Route
                            path={routes.dataRoute.timesheet}
                            element={<TimesheetPage />}
                        />

                        {/* Read-only views available to Supervisor and all non-client roles */}
                        {userRole !== ERole.Client && userRole !== ERole.LimitedClient && (
                            <>
                                <Route
                                    path={routes.dataRoute.periodicsDashboard}
                                    element={<PeriodicDashboardPage />}
                                />
                                <Route
                                    path={routes.dataRoute.filledForms}
                                    element={<FilledFormsPage />}
                                />
                                <Route
                                    path={routes.dataRoute.filledForm}
                                    element={<FilledFormPage />}
                                />
                                <Route
                                    path={routes.dataRoute.workingRoad}
                                    element={<WorkingRoadPage />}
                                />
                                <Route path={routes.dataRoute.employeeFeedback}>
                                    <Route index element={<TicketsPage />} />
                                    <Route
                                        path={routes.dataRoute.employeeFeedbackDetails}
                                        element={<DetailedTicketPage />}
                                    />
                                </Route>
                                <Route
                                    path={routes.dataRoute.scans}
                                    element={<ScansPage />}
                                />
                            </>
                        )}

                        {/* Routes hidden from clients and Supervisor */}
                        {userRole !== ERole.Client && userRole !== ERole.LimitedClient && userRole !== ERole.Supervisor && (
                            <>
                                <Route path={routes.dataRoute.periodics}>
                                    <Route index element={<PeriodicPage />} />
                                    <Route
                                        path={routes.dataRoute.periodicAdd}
                                        element={<PeriodicAddPage />}
                                    />
                                    <Route
                                        path={routes.dataRoute.periodicValidation}
                                        element={<PeriodicReviewManager />}
                                    />
                                </Route>
                                <Route
                                    path={routes.dataRoute.trashcanDashboard}
                                    element={<WasteCanDashboardPage />}
                                />
                                <Route
                                    path={routes.dataRoute.trashcanSensor}
                                    element={<StepperSensorPage />}
                                />
                                <Route
                                    path={routes.dataRoute.formTemplates}
                                    element={<FormTemplatesPage />}
                                />
                                <Route
                                    path={routes.dataRoute.formTemplateEdit}
                                    element={<FormTemplateEditPage />}
                                />
                                <Route
                                    path={routes.dataRoute.eslTemplates}>
                                    <Route index element={<EslTemplatesPage />} />
                                    <Route
                                        path={routes.dataRoute.eslTemplateEditor}
                                        element={<EslTemplateEditor />}
                                    />
                                </Route>
                            </>
                        )}
                    </Route>

                    {/* Table Routes - only visible to admin-tier roles (incl. read-only Manager) */}
                    {userRole !== ERole.Client && userRole !== ERole.LimitedClient && userRole !== ERole.Supervisor && (
                        <Route path={routes.tableRoute.table}>
                            <Route index element={<TablePage />} />
                            <Route path={routes.tableRoute.profiles}>
                                <Route index element={<ProfilePage />} />
                                <Route
                                    path={routes.tableRoute.profileAdd}
                                    element={<ProfileAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.profileEdit}
                                    element={<ProfileEditPage />}
                                />
                            </Route>

                            <Route path={routes.tableRoute.companyEdit} element={<CompanyEditPage />} />

                            <Route path={routes.tableRoute.buildings}>
                                <Route index element={<BuildingPage />} />
                                <Route
                                    path={routes.tableRoute.buildingAdd}
                                    element={<BuildingAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.buildingEdit}
                                    element={<BuildingEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.locals}>
                                <Route index element={<LocalPage />} />
                                <Route
                                    path={routes.tableRoute.localAdd}
                                    element={<LocalAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.localEdit}
                                    element={<LocalEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.floors}>
                                <Route index element={<FloorPage />} />
                                <Route
                                    path={routes.tableRoute.floorAdd}
                                    element={<FloorAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.floorEdit}
                                    element={<FloorEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.propertyGroups}>
                                <Route index element={<PropertyGroupPage />} />
                                <Route
                                    path={routes.tableRoute.propertyGroupAdd}
                                    element={<PropertyGroupAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.propertyGroupEdit}
                                    element={<PropertyGroupEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.routes}>
                                <Route index element={<RoutePage />} />
                                <Route
                                    path={routes.tableRoute.routeAdd}
                                    element={<RouteAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.routeEdit}
                                    element={<RouteEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.tasks}>
                                <Route index element={<TaskPage />} />
                            </Route>
                            <Route path={routes.tableRoute.users}>
                                <Route index element={<UserPage />} />
                                <Route
                                    path={routes.tableRoute.userAdd}
                                    element={<UserAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.userEdit}
                                    element={<UserEditPage />}
                                />
                            </Route>
                            <Route path={routes.tableRoute.contacts}>
                                <Route index element={<ContactPage />} />
                                <Route
                                    path={routes.tableRoute.contactAdd}
                                    element={<ContactAddPage />}
                                />
                                <Route
                                    path={routes.tableRoute.contactEdit}
                                    element={<ContactEditPage />}
                                />
                            </Route>
                        </Route>
                    )}
                </Route>

                {/* Service routes - visible to all roles */}
                <Route
                    path={routes.servicePlus.installation}
                    element={<InstallationPage />}
                />
                <Route
                    path={routes.servicePlus.setupComplete}
                    element={<SuccessScreen />}
                />
                <Route
                    path={routes.servicePlus.setupError}
                    element={<ErrorScreen />}
                />
                </Route>
            </Route>

            {/* default route */}
            <Route path={routes.notFound} element={<NotFound />} />
        </Route>
    );
};

const router = createBrowserRouter(
    createRoutesFromElements(getRoutesForRole())
);



const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: CACHE_TIMES.DEFAULT_STALE,
        },
    },
}); const App = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    );
};

export default App;
